import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  type ReactNode
} from "react";

import {
  ActivityHost,
  type ActivityHostBridgeEvents,
  type ActivityHostCommand,
  type ActivityHostReadyEvent,
  type ActivityHostRenderContext
} from "@/features/activity-host";
import {
  createEventBridge,
  type EventBridge
} from "@/game/core/event-bridge";

import type {
  PlayRendererDefinition,
  PlayRendererHostProps
} from "./contracts";

interface RendererCallbacks {
  readonly onCommand: (command: ActivityHostCommand) => void;
  readonly onReady: (event: ActivityHostReadyEvent) => void;
}

interface AutoReadyActivityProps<TAction> {
  readonly bridge: EventBridge<ActivityHostBridgeEvents<TAction>>;
  readonly children: ReactNode;
}

export interface RegisteredActivityProps<
  TPayload,
  TState,
  TAction,
  TRenderModel
> extends Omit<PlayRendererHostProps, "sessionKey"> {
  readonly definition: PlayRendererDefinition<
    TPayload,
    TState,
    TAction,
    TRenderModel
  >;
}

function createForwardingBridge<TAction>(
  callbacks: RendererCallbacks
): EventBridge<ActivityHostBridgeEvents<TAction>> {
  const bridge = createEventBridge<ActivityHostBridgeEvents<TAction>>();

  bridge.subscribe("command", callbacks.onCommand);
  bridge.subscribe("ready", callbacks.onReady);

  return bridge;
}

function AutoReadyActivity<TAction>({
  bridge,
  children
}: AutoReadyActivityProps<TAction>) {
  useLayoutEffect(() => {
    bridge.emit("ready", {});
  }, [bridge]);

  return <>{children}</>;
}

export function RegisteredActivity<
  TPayload,
  TState,
  TAction,
  TRenderModel
>({
  caption,
  definition,
  level,
  nowMs,
  onCommand,
  onComplete,
  onExit,
  onLevelError,
  onReady,
  reducedMotion
}: RegisteredActivityProps<TPayload, TState, TAction, TRenderModel>) {
  const bridge = useMemo(
    () => createForwardingBridge<TAction>({ onCommand, onReady }),
    [onCommand, onReady]
  );

  useEffect(() => {
    bridge.emit("caption", caption);
  }, [bridge, caption]);

  const renderActivity = useCallback(
    (context: ActivityHostRenderContext<TAction, TRenderModel>) => {
      const activity = definition.renderActivity({ bridge, context, level });

      return definition.autoReady === false ? (
        activity
      ) : (
        <AutoReadyActivity bridge={bridge}>{activity}</AutoReadyActivity>
      );
    },
    [bridge, definition, level]
  );

  return (
    <ActivityHost
      actions={definition.actions}
      adapter={definition.adapter}
      bridge={bridge}
      instruction={{ caption: level.instruction, title: level.title }}
      levelId={level.id}
      levelTitle={level.title}
      nowMs={nowMs}
      onComplete={onComplete}
      onExit={onExit}
      onLevelError={onLevelError}
      payload={level.payload}
      reducedMotion={reducedMotion}
      renderActivity={renderActivity}
    />
  );
}
